import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { ContractProvider } from '../../core/domain/providers/contract.provider';
import { Id, SelectedProduct } from '../domain/data';
import { IdInterface, OrderType, ProductInterface, Transaction } from '../../core';
import { OperationProvider } from '../../core/domain/providers/operation.provider';
import { ProductProvider } from '../../core/domain/providers/product.provider';
import { UseCaseWithParam } from '../../core/domain/usecases/common/use.case.with.params';

export interface CreateOperationUseCaseParams {
    productCode: IdInterface;
    clientCode?: IdInterface;
}

@Injectable()
export class CreateRefundOperationUseCase implements UseCaseWithParam<CreateOperationUseCaseParams, Observable<Transaction<void>>> {
    constructor(
        private contractProvider: ContractProvider,
        private operationProvider: OperationProvider,
        private productProvider: ProductProvider
    ) {}

    bind(params: CreateOperationUseCaseParams): Observable<Transaction<void>> {
        return this.contractProvider.current().pipe(
            switchMap((transaction) =>
                transaction.map(
                    (contract) =>
                        this.productProvider.findMine(contract.encryptedCode).pipe(
                            switchMap((transactionProduct) =>
                                transactionProduct.map(
                                    (myProducts) => {
                                        const myProduct: ProductInterface = myProducts.find((it) =>
                                            it.encryptedCode.equals(params.productCode)
                                        );

                                        if (myProduct) {
                                            const selectedProduct = new SelectedProduct(contract.encryptedCode);
                                            selectedProduct.productId = myProduct.id as Id;
                                            selectedProduct.code = myProduct.code as Id;
                                            selectedProduct.encryptedCode = myProduct.encryptedCode as Id;
                                            selectedProduct.isin = myProduct.isin;
                                            selectedProduct.name = myProduct.name;
                                            selectedProduct.isExonerated = true;
                                            selectedProduct.amount = myProduct.amount;
                                            selectedProduct.shares = myProduct.shares;
                                            selectedProduct.order.type = OrderType.REFUND;
                                            selectedProduct.order.destinationContractCode = contract.encryptedCode;

                                            return this.productProvider.detail(myProduct.encryptedCode).pipe(
                                                switchMap((transactionProductDetail) =>
                                                    transactionProductDetail.map(
                                                        (product) =>
                                                            this.operationProvider.create(
                                                                transaction.data,
                                                                product,
                                                                selectedProduct,
                                                                true,
                                                                false,
                                                                false,
                                                                params.clientCode
                                                            ),
                                                        (throwable) => of(Transaction.error(throwable.message))
                                                    )
                                                )
                                            );
                                        }

                                        return of(Transaction.error<void>());
                                    },
                                    (throwable) => of(Transaction.error(throwable.message))
                                )
                            )
                        ),
                    (throwable) => of(Transaction.error(throwable.message))
                )
            )
        );
    }
}
